/**
 * Copying that survives the SS OS iframe.
 *
 * Root cause of "We could not copy automatically" on every admin copy button
 * (decision C6/A8): the admin is embedded cross-origin in the Web OS
 * (os.sheahanstudios.com → review.sheahanstudios.com) by an <iframe> with no
 * allow="clipboard-write". The default permissions-policy allowlist for
 * clipboard-write is 'self', so the embedded document is denied it and
 * navigator.clipboard.writeText() rejects with NotAllowedError every time,
 * on HTTPS, in every browser. The legacy selection copy is gated on user
 * activation rather than permissions policy, so it still works in there.
 *
 * Callers must already hold the text: an await before this call can cost the
 * click its transient user activation, which both paths depend on.
 */

export type CopyOutcome = 'clipboard' | 'selection' | 'failed';

export async function copyText(text: string): Promise<CopyOutcome> {
  if (!text) return 'failed';
  try {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      await navigator.clipboard.writeText(text);
      return 'clipboard';
    }
  } catch {
    // Denied by permissions policy, or refused by the browser: try the old way.
  }
  return copyBySelection(text) ? 'selection' : 'failed';
}

/** Selects the text in an off-screen field and asks the browser to copy it. */
export function copyBySelection(text: string): boolean {
  if (typeof document === 'undefined' || !document.body) return false;
  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  // Off-screen rather than hidden: a display:none field cannot hold a selection.
  field.style.position = 'fixed';
  field.style.top = '0';
  field.style.left = '-9999px';
  const previous = document.activeElement as { focus?: () => void } | null;
  document.body.appendChild(field);
  try {
    field.select();
    field.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    field.remove();
    previous?.focus?.();
  }
}
